'use client'
import type { FC, MouseEvent, ReactNode } from 'react'
import {
  Box,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack
} from '@chakra-ui/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { MdKeyboardArrowDown } from 'react-icons/md'

export interface MenuItemChild {
  id: string
  text: string
  path: string
  isExternal?: boolean
}

export interface MenuItem {
  id: string
  text: string
  /** `null` for an entry that only opens its dropdown. */
  path: string | null
  isExternal?: boolean
  isCurrent?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  other?: any[]
  onHoverEnter?: () => void
  onHoverLeave?: () => void
  isDarkBackground?: boolean
  /** Renders the item as a dropdown; the item itself still links to `path` when set. */
  subItems?: MenuItemChild[]
}

export const externalLinkProps = { target: '_blank', rel: 'noopener noreferrer' }

const MenuLink: FC<{
  path: string
  isExternal?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  children: ReactNode
}> = ({ path, isExternal, onClick, children }) => (
  <Link href={path} onClick={onClick} {...(isExternal ? externalLinkProps : {})}>
    {children}
  </Link>
)

export const MenuItemComponent: FC<MenuItem> = ({
  text,
  path,
  isExternal,
  isCurrent,
  onClick,
  subItems
}) => {
  if (subItems?.length) {
    return <MenuDropdownItem {...{ text, path, isExternal, isCurrent, onClick, subItems }} />
  }

  if (!path) return null

  return (
    <MenuLink path={path} isExternal={isExternal} onClick={onClick}>
      <MenuItemText text={text} isCurrent={isCurrent} />
    </MenuLink>
  )
}

const MenuItemText: FC<{ text: string; isCurrent?: boolean }> = ({ text, isCurrent }) => (
  <Text
    fontSize="0.95rem"
    fontWeight={500}
    color={isCurrent ? 'text.default' : 'text.secondary'}
    transition="color 0.2s ease"
    _hover={{ color: 'text.default' }}
  >
    {text}
  </Text>
)

const MenuDropdownItem: FC<
  Pick<MenuItem, 'text' | 'path' | 'isExternal' | 'isCurrent' | 'onClick'> & {
    subItems: MenuItemChild[]
  }
> = ({ text, path, isExternal, isCurrent, onClick, subItems }) => {
  const pathname = usePathname()
  const isSectionCurrent =
    isCurrent ||
    (!!path && !!pathname?.startsWith(`${path}/`)) ||
    subItems.some((sub) => sub.path === pathname)

  const trigger = (
    <HStack spacing={1}>
      <MenuItemText text={text} isCurrent={isSectionCurrent} />
      <Box as={MdKeyboardArrowDown} color="text.secondary" aria-hidden />
    </HStack>
  )

  return (
    <Popover trigger="hover" placement="bottom-start" openDelay={0} closeDelay={150} isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            {/* PopoverTrigger needs a ref-forwarding child; MenuLink is a plain component */}
            {path ? (
              <Box display="inline-flex">
                <MenuLink path={path} isExternal={isExternal} onClick={onClick}>
                  {trigger}
                </MenuLink>
              </Box>
            ) : (
              <Box as="button" type="button" cursor="default">
                {trigger}
              </Box>
            )}
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              // above the fixed top nav (zIndex 9000)
              rootProps={{ zIndex: 9001 }}
              bg="background.default"
              w="auto"
              minW="220px"
              p={2}
              border="1px solid"
              borderColor="border.default"
              borderRadius="card"
              boxShadow="cardHover"
              _focusVisible={{ outline: 'none' }}
            >
              <VStack align="stretch" spacing={0}>
                {subItems.map((sub) => (
                  <Box
                    key={sub.id}
                    as={Link}
                    href={sub.path}
                    onClick={onClose}
                    {...(sub.isExternal ? externalLinkProps : {})}
                    px={3}
                    py={2}
                    borderRadius="button"
                    fontSize="0.95rem"
                    fontWeight={500}
                    color={pathname === sub.path ? 'text.default' : 'text.secondary'}
                    transition="background 0.2s ease, color 0.2s ease"
                    _hover={{ bg: 'background.surface', color: 'text.default' }}
                  >
                    {sub.text}
                  </Box>
                ))}
              </VStack>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  )
}
